(() => {
  if (customElements.get('vnz-purchase-options')) return;

  // Prestige preserves the form during partial renders; retain state for full renders too.
  const states = new Map();
  const root = () => window.Shopify?.routes?.root || '/';
  const emit = (target, name, detail) => target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  const owner = (form) => form?.querySelector('vnz-purchase-options');

  class VnzPurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const config = this.querySelector('[data-vnz-config]');
      if (!config) {
        // Elements created while parsing quick-buy HTML connect before their children exist.
        if (!this.markupRetryQueued) {
          this.markupRetryQueued = true;
          queueMicrotask(() => {
            this.markupRetryQueued = false;
            if (this.isConnected && this.querySelector('[data-vnz-config]')) this.connectedCallback();
          });
        }
        return;
      }
      try { this.config = JSON.parse(config.textContent); } catch { return; }
      this.form = this.closest('form');
      if (!this.form || !Array.isArray(this.config.variants)) return;
      this.initialized = true;
      const key = this.form.getAttribute('id');
      if (!states.has(key)) states.set(key, { includeInsert: false, pending: false, needsReview: false, error: '' });
      this.state = states.get(key);
      this.busyButtons = new Map();
      this.checkbox = this.querySelector('[data-vnz-insert]');
      this.checkbox.checked = this.state.includeInsert;
      this.addEventListener('change', (event) => {
        if (event.target === this.checkbox) {
          this.state.includeInsert = this.checkbox.checked;
          if (!this.state.needsReview) this.state.error = '';
          this.sync();
        }
      });
      this.addEventListener('input', () => this.updateQuote());
      this.querySelector('[data-vnz-quote-link]').addEventListener('click', (event) => {
        if (!this.updateQuote(true)) event.preventDefault();
      });
      this.sync();
    }

    variant() {
      const id = this.form.elements.namedItem('id')?.value;
      return this.config.variants.find((variant) => String(variant.id) === String(id));
    }

    selectedSize() {
      const selected = Array.from(this.form.elements).find((input) => input.dataset.optionPosition === '1' && (input.checked || input.tagName === 'SELECT'));
      return this.config.options?.find((option) => String(option.id) === String(selected?.value))?.value || this.variant()?.size;
    }

    isQuote() {
      const size = this.selectedSize();
      return this.config.variants.some((variant) => variant.size === size && variant.custom);
    }

    relatedElements(selector) {
      const id = this.form.getAttribute('id');
      return Array.from(document.querySelectorAll(selector)).filter((element) => element.getAttribute('form') === id);
    }

    sync() {
      if (!this.initialized) return;
      const variant = this.variant();
      const quote = this.isQuote();
      const waiting = variant?.size !== this.selectedSize();
      const insert = this.config.bundleMode ? variant?.bundle : variant?.insert;
      const insertPrice = this.config.bundleMode ? insert?.surcharge : insert?.price;
      const total = this.config.bundleMode ? insert?.price : variant?.total;
      // A quote is available to request even though its placeholder variant cannot be bought.
      for (const input of this.form.elements) {
        if (input.dataset.optionPosition !== '1') continue;
        const option = this.config.options?.find((value) => String(value.id) === String(input.value));
        if (this.config.variants.some((value) => value.custom && value.size === option?.value)) {
          for (const label of input.labels || []) label.classList.remove('is-disabled');
        }
      }
      this.querySelector('[data-vnz-standard]').hidden = quote;
      this.querySelector('[data-vnz-quote]').hidden = !quote;
      for (const input of this.querySelectorAll('[data-vnz-quote] input, [data-vnz-quote] textarea')) input.disabled = !quote;
      this.form.toggleAttribute('data-vnz-quote-active', quote);
      this.form.toggleAttribute('data-vnz-insert-active', this.state.includeInsert || waiting);
      this.form.toggleAttribute('data-vnz-review-needed', this.state.needsReview);
      for (const element of this.relatedElements('product-sticky-bar[form], buy-buttons[form]')) {
        element.toggleAttribute('data-vnz-quote-active', quote);
      }
      const info = this.closest('.product-info');
      for (const element of info?.querySelectorAll('[data-block-type="price"], [data-block-type="payment-terms"], [data-block-type="quantity-selector"]') || []) {
        element.toggleAttribute('data-vnz-quote-hidden', quote);
      }
      this.checkbox.checked = this.state.includeInsert;
      this.checkbox.disabled = this.state.pending || waiting || this.state.needsReview || ((!insert?.available || !variant?.available) && !this.state.includeInsert);
      this.querySelector('[data-vnz-insert-price]').textContent = insert ? `(+${insertPrice})` : '';
      this.querySelector('[data-vnz-availability]').textContent = waiting ? 'Updating size…' : !insert?.available ? 'A matching insert is currently unavailable for this size. You can order the cover on its own.' : 'The insert matches your selected size. One insert is added for each cover.';
      this.querySelector('[data-vnz-total]').textContent = this.state.includeInsert && insert ? `Cover ${variant.price} + insert ${insertPrice} = ${total} per cushion` : `Cover only: ${variant?.price || ''}`;
      this.showError(this.state.error);
      const review = this.querySelector('[data-vnz-review]');
      review.hidden = !this.state.needsReview;
      review.href = `${root()}cart`;
      this.setBusy(this.state.pending || this.state.needsReview);
      this.updateQuote();
    }

    setBusy(busy) {
      // Include sticky buttons associated with this form, without enabling sold-out buttons.
      const buttons = Array.from(this.form.elements).filter((element) => element.type === 'submit');
      if (busy) {
        for (const button of buttons) {
          if (!this.busyButtons.has(button)) this.busyButtons.set(button, { disabled: button.disabled, busy: button.getAttribute('aria-busy') });
          button.disabled = true;
          if (this.state.pending) button.setAttribute('aria-busy', 'true');
          else if (this.busyButtons.get(button).busy === null) button.removeAttribute('aria-busy');
          else button.setAttribute('aria-busy', this.busyButtons.get(button).busy);
        }
      } else {
        for (const [button, previous] of this.busyButtons) {
          if (!button.isConnected) continue;
          button.disabled = previous.disabled;
          if (previous.busy === null) button.removeAttribute('aria-busy');
          else button.setAttribute('aria-busy', previous.busy);
        }
        this.busyButtons.clear();
      }
    }

    showError(message) {
      const error = this.querySelector('[data-vnz-error]');
      error.textContent = message || '';
      error.hidden = !message;
    }

    updateQuote(validate = false) {
      const width = this.querySelector('[data-vnz-width]');
      const height = this.querySelector('[data-vnz-height]');
      const quantity = this.querySelector('[data-vnz-quote-quantity]');
      const valid = Number(width.value) > 0 && Number(height.value) > 0 && Number.isSafeInteger(Number(quantity.value)) && Number(quantity.value) > 0 && [width, height, quantity].every((input) => input.checkValidity());
      if (validate && !valid) {
        this.showError('Please enter a width, height and whole-number quantity greater than zero.');
        [width, height, quantity].find((input) => !input.value || Number(input.value) <= 0 || !input.checkValidity())?.focus();
        return false;
      }
      const dimensions = valid ? `${width.value} x ${height.value} cm` : 'Please specify width x height in cm';
      const body = [
        `Product: ${this.dataset.productTitle}`,
        `Link: ${new URL(this.dataset.productUrl, window.location.origin).href}`,
        `Custom size: ${dimensions}`,
        `Quantity: ${quantity.value || '1'}`,
        `Notes: ${this.querySelector('[data-vnz-notes]').value.trim()}`,
        '', 'Please confirm availability and a quote before I place an order.'
      ].join('\n');
      this.querySelector('[data-vnz-quote-link]').href = `mailto:${this.dataset.email}?subject=${encodeURIComponent(`Custom size enquiry — ${this.dataset.productTitle}`)}&body=${encodeURIComponent(body)}`;
      if (validate && !this.state.error) this.showError('');
      return true;
    }

    intercept(event) {
      if (!this.initialized) return;
      const variant = this.variant();
      const waiting = variant?.size !== this.selectedSize();
      const nativeBusy = !this.state.pending && Array.from(this.form.elements).some((input) => input.type === 'submit' && input.getAttribute('aria-busy') === 'true');
      if (!this.isQuote() && !this.state.includeInsert && !this.state.pending && !this.state.needsReview && !waiting && !nativeBusy) return;
      // Prestige's handler does not check defaultPrevented, so stop propagation as well.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.isQuote()) { this.querySelector('[data-vnz-width]').focus(); return; }
      if (this.state.pending || this.state.needsReview) return;
      if (nativeBusy) { this.showError('Your previous add-to-cart request is still being processed. Please wait a moment.'); return; }
      if (waiting) { this.showError('Your selected size is updating. Please try again in a moment.'); return; }
      const insert = this.config.bundleMode ? variant?.bundle : variant?.insert;
      if (!variant?.available || !insert?.id || !insert.available) {
        this.showError('The cover and matching insert are not both available. Choose another size or uncheck the insert to order the cover only.');
        return;
      }
      if (!this.form.checkValidity()) { this.form.reportValidity(); return; }
      const data = new FormData(this.form);
      const quantity = Number(data.get('quantity') || 1);
      if (!Number.isSafeInteger(quantity) || quantity < 1) { this.showError('Please enter a whole-number quantity greater than zero.'); return; }
      void this.add(variant, quantity, data);
    }

    async add(variant, quantity, data) {
      this.state.pending = true;
      this.state.error = '';
      this.sync();
      emit(document.documentElement, 'theme:loading:start');
      const sections = [];
      emit(document.documentElement, 'cart:prepare-bundled-sections', { sections });
      const properties = {};
      for (const [key, value] of data) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      // The pilot bundle owns both component quantities and their shared inventory.
      const cover = { id: this.config.bundleMode ? variant.bundle.id : variant.id, quantity };
      if (Object.keys(properties).length) cover.properties = properties;
      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) cover.selling_plan = sellingPlan;
      const onSuccessDo = data.get('on_success');
      let added = false;
      let failureMessage = 'We could not confirm this request.';
      try {
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: this.config.bundleMode ? [cover] : [cover, { id: variant.insert.id, quantity }], sections, sections_url: `${root()}variants/${variant.id}` })
        });
        const result = await response.json();
        if (!response.ok) {
          failureMessage = typeof result.description === 'string' ? result.description : 'The requested quantity is not available.';
          throw new Error(failureMessage);
        }
        added = true;
        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart' || window.location.pathname === `${root()}cart`) {
          window.location.assign(`${root()}cart`);
          return;
        }
        const cartResponse = await fetch(`${root()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        const cart = await cartResponse.json();
        cart.sections = result.sections;
        if (sections.some((section) => !cart.sections?.[section])) throw new Error('Cart section refresh failed');
        emit(this.form, 'variant:add', { items: result.items || [result], cart, onSuccessDo });
        emit(document.documentElement, 'cart:change', { baseEvent: 'variant:add', cart, onSuccessDo });
      } catch {
        // A failed or interrupted multi-item request can leave cart changes. Never retry it automatically.
        this.state.needsReview = true;
        this.state.error = added
          ? 'Your items were added, but the cart could not refresh. Please review your cart before adding more.'
          : `${failureMessage} Please review your cart before trying again.`;
        const escapedError = this.state.error.replace(/[&<>]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[character]);
        emit(this.form, 'cart:error', { error: escapedError });
        emit(document.documentElement, 'cart:refresh');
      } finally {
        emit(document.documentElement, 'theme:loading:end');
        this.state.pending = false;
        const current = owner(document.getElementById(this.form.getAttribute('id')));
        if (current?.initialized) current.sync();
      }
    }
  }

  customElements.define('vnz-purchase-options', VnzPurchaseOptions);

  document.addEventListener('submit', (event) => owner(event.target)?.intercept(event), true);
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('vnz-purchase-options input')) return;
    const form = event.target.form;
    owner(form)?.sync();
  }, true);
  document.addEventListener('variant:change', (event) => owner(event.target)?.sync());
  document.addEventListener('product:rerender', (event) => {
    const id = event.target.getAttribute('id');
    queueMicrotask(() => owner(document.getElementById(id))?.sync());
  }, true);
})();
